import { useMemo, useState } from 'react'
import { CheckCircle2, XCircle, Store, Eye, Clock } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import {
  useAdminPendingApprovals,
  useApproveRequestMutation,
  useRejectRequestMutation,
} from '../../hooks/use-admin'
import { EnterpriseTable, type Column } from '../../components/tables/enterprise-table'
import { ApprovalDialog } from '../../components/dialogs/approval-dialog'
import { UserDetailDrawer } from '../../components/dialogs/user-detail-drawer'
import type { ApprovalRequest } from '../../types/admin.types'

export function SellerRequestsPage() {
  const { data: pendingApprovals, isLoading } = useAdminPendingApprovals()
  const approveMutation = useApproveRequestMutation()
  const rejectMutation = useRejectRequestMutation()

  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null)
  const [dialogMode, setDialogMode] = useState<'approve' | 'reject'>('approve')
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [inspectRequest, setInspectRequest] = useState<ApprovalRequest | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Filter requests for SELLER type
  const sellerRequests = useMemo(() => {
    return (pendingApprovals ?? []).filter((req) => req.requestType === 'SELLER')
  }, [pendingApprovals])

  const handleOpenDialog = (request: ApprovalRequest, mode: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setDialogMode(mode)
    setIsDialogOpen(true)
  }

  const handleConfirmAction = async (
    id: number,
    notes?: string,
    riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH',
    planAssignment?: any,
  ) => {
    if (dialogMode === 'approve') {
      await approveMutation.mutateAsync({
        id,
        payload: { remarks: notes, riskLevel, ...(planAssignment ?? {}) },
      })
    } else {
      await rejectMutation.mutateAsync({ id, payload: { remarks: notes } })
    }
  }

  const handleInspectRequest = (req: ApprovalRequest) => {
    setInspectRequest(req)
    setIsDrawerOpen(true)
  }

  const columns: Column<ApprovalRequest>[] = [
    {
      key: 'applicant',
      header: 'Applicant',
      sortable: true,
      sortValue: (r) => `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''}`,
      accessor: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-display text-xs font-bold">
            {r.user?.firstName?.[0] ?? 'S'}
            {r.user?.lastName?.[0] ?? 'R'}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {r.user?.firstName} {r.user?.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{r.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Request Type',
      accessor: () => <Badge variant="secondary">SELLER ACCOUNT</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (r) => (
        <Badge variant={r.status === 'PENDING' ? 'warning' : 'default'} className="gap-1">
          <Clock className="size-3" /> {r.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Submitted',
      sortable: true,
      sortValue: (r) => new Date(r.createdAt).getTime(),
      accessor: (r) => (
        <span className="text-xs text-muted-foreground">
          {new Date(r.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="Seller Onboarding"
        title="Seller Approval Requests"
        description="Verify identity, review applications, and grant listing & selling privileges for individual merchants."
        align="left"
      />

      {/* Overview Metric */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
              <Store className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Pending Requests</p>
              <p className="font-display text-xl font-bold text-amber-500">
                {sellerRequests.length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
              <CheckCircle2 className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Verification Status</p>
              <p className="font-display text-xl font-bold text-foreground">Active Gate</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
              <Clock className="size-5" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Avg Review Time</p>
              <p className="font-display text-xl font-bold text-foreground">&lt; 15 mins</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <EnterpriseTable
        data={sellerRequests}
        columns={columns}
        isLoading={isLoading}
        searchPlaceholder="Search applicant by name or email..."
        searchableKeys={[(r) => `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''} ${r.user?.email ?? ''}`]}
        actions={(r) => (
          <div className="flex items-center justify-end gap-2">
            <Button
                variant="outline"
                size="sm"
                onClick={() => handleInspectRequest(r)}
                className="h-8 gap-1 text-xs"
              >
                <Eye className="size-3.5" /> Details
              </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenDialog(r, 'reject')}
              disabled={rejectMutation.isPending}
              className="h-8 gap-1 text-xs text-red-500 hover:text-red-600 border-red-500/30 hover:bg-red-500/10"
            >
              <XCircle className="size-3.5" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={() => handleOpenDialog(r, 'approve')}
              disabled={approveMutation.isPending}
              className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              <CheckCircle2 className="size-3.5" /> Approve
            </Button>
          </div>
        )}
        emptyTitle="No pending seller requests"
        emptyDescription="All submitted seller applications have been verified and processed."
      />

      {/* Approval / Rejection Modal Dialog */}
      <ApprovalDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        request={selectedRequest}
        mode={dialogMode}
        onSubmit={handleConfirmAction}
        isPending={approveMutation.isPending || rejectMutation.isPending}
      />

      {/* User Inspection Drawer — shows full application data */}
      <UserDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        request={inspectRequest}
      />
    </div>
  )
}
